import { useState } from 'react';

import Text from '@cartwhl/ui/base/TextNew';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import Button from '@cartwhl/ui/input/Button';

import type { DashboardWorkSection as DashboardWorkSectionType } from '../types';
import DashboardWorkCard from './DashboardWorkCard';
import './DashboardWorkList.css';

const CARD_ENTRY_DELAY_MS = 150;
const CARD_STAGGER_MS = 35;

export interface DashboardWorkSectionProps {
  section: DashboardWorkSectionType;
  showDivider?: boolean;
}

export default function DashboardWorkSection({
  section,
  showDivider,
}: DashboardWorkSectionProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (section.items.length === 0) {
    return null;
  }

  return (
    <section
      className={`flex flex-col ${showDivider ? 'border-primary border-t pt-8' : ''}`}
    >
      <div className="flex items-center justify-between px-2">
        <Text as="h2" token="20" leading="headline" className="text-primary">
          {section.title}{' '}
          <span className="text-tertiary">({section.items.length})</span>
        </Text>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          icon={
            <ChevronDownIcon
              className={`${collapsed ? '-rotate-90' : 'rotate-0'} transition-transform`}
            />
          }
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>

      <div
        className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
          collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`transition-[padding-top,opacity] duration-200 ease-out ${
              collapsed ? 'pt-0 opacity-0' : 'pt-4 opacity-100'
            }`}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {section.items.map((item, index) => (
                <div
                  key={`${section.id}-${collapsed ? 'closed' : 'open'}-${item.kind}-${item.id}`}
                  className="dashboard-work-card-enter"
                  style={{ animationDelay: getWorkCardAnimationDelay(index) }}
                >
                  <DashboardWorkCard item={item} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function getWorkCardAnimationDelay(index: number) {
  return `${CARD_ENTRY_DELAY_MS + Math.min(index, 11) * CARD_STAGGER_MS}ms`;
}
